import React from "react";
import { useTranslation } from "react-i18next";
import { DataTable, Column } from "@/components/ui/data-table";
import { StaffWithRelations } from "@/services/staffService";
import { RequestStatus } from "@/types/request";

import { StatusCell } from "./table-cells/StatusCell";
import { NameCell } from "./table-cells/NameCell";
import { EmailCell } from "./table-cells/EmailCell";
import { PhoneCell } from "./table-cells/PhoneCell";
import { RoleCell } from "./table-cells/RoleCell";
import { TeamsCell } from "./table-cells/TeamsCell";
import { TasksCell } from "./table-cells/TasksCell";
import { ActionsCell } from "./table-cells/ActionsCell";
import { getStaffPresentationLabels } from "../utils/staffExport";
import { Role } from "@/types/user";

type SortOrder = "asc" | "desc" | null;

interface StaffTableProps {
  data: StaffWithRelations[];
  isLoading: boolean;
  sortBy: string | null;
  sortOrder: SortOrder;
  onSort: (field: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onDelete: (id: string) => void;
  onStatusToggle: (staff: StaffWithRelations) => void;
  staffStatusMap: Map<string, "on-site" | "not-on-site">;
}

export const StaffTable: React.FC<StaffTableProps> = ({
  data,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
  onDelete,
  onStatusToggle,
  staffStatusMap,
}) => {
  const { t } = useTranslation();
  const labels = getStaffPresentationLabels(t);
  const columns: Column<StaffWithRelations>[] = [
    {
      key: "isActive",
      header: labels.status,
      className: "text-center",
      sortable: true,
      render: (_, user) => (
        <StatusCell
          isActive={
            user.staff ? user.staff.isActive : user.status !== "INACTIVE"
          }
          onToggle={() => onStatusToggle(user)}
        />
      ),
    },
    {
      key: "name",
      header: labels.name,
      sortable: true,
      className: "text-center",
      render: (_, user) => {
        const status =
          (user.staff?.id ? staffStatusMap.get(user.staff.id) : undefined) ??
          staffStatusMap.get(user.id);
        return (
          <NameCell
            user={user}
            position={user.staff?.position}
            isOnSite={status === "on-site"}
          />
        );
      },
    },
    {
      key: "email",
      header: labels.email,
      sortable: true,
      className: "text-center",
      render: (value) => <EmailCell email={value} />,
    },
    {
      key: "phone",
      header: labels.phone,
      sortable: true,
      className: "text-center",
      render: (value) => <PhoneCell phone={value} />,
    },
    {
      key: "role",
      header: labels.role,
      className: "text-center",
      sortable: true,
      render: (_, user) => (
        <RoleCell
          role={
            user.role === Role.FRONT_DESK
              ? user.customRoles
                  ?.map((customRole) => customRole.customRoleName)
                  .join(", ") || "Frontdesk"
              : "Staff"
          }
        />
      ),
    },
    {
      key: "teams",
      header: labels.group,
      className: "text-center",
      sortable: true,
      render: (_, user) =>
        user.staff ? (
          <TeamsCell staffId={user.id} userTeams={user.staff.userTeams} />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "tasks",
      header: labels.assignedTasks,
      className: "text-center",
      sortable: true,
      render: (_, user) =>
        user.staff ? (
          <TasksCell
            taskCount={
              user.staff.tasks.filter((t) => t.status !== RequestStatus.DONE)
                .length
            }
            staffId={user.staff.id}
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "actions",
      header: "Actions",
      className: "text-center",
      render: (_, user) => (
        <ActionsCell
          userId={user.id}
          onDelete={onDelete}
          isStaffAppUser={Boolean(user.staff)}
        />
      ),
    },
  ];

  return (
    <DataTable
      data={data}
      columns={columns}
      loading={isLoading}
      sorting={{
        field: sortBy,
        direction: sortOrder,
        onSort,
      }}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
      }}
      withPagination={true}
      withSorting={true}
      emptyMessage="No staff members found matching your criteria"
    />
  );
};
